import { useAadhaarKyc } from "../../hooks/useAadhaarKyc";

const Spinner = () => (
  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
);

const CheckIcon = ({ size = 16, color = "#16a34a" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default function AadhaarKycWidget({ dob, onVerified, onAutoFill }) {
  const { status, signInUrl, error, digioVerified, initiate, openDigilocker, reset } =
    useAadhaarKyc({ dob, onVerified });

  // ── idle / failed ──
  if (status === "idle" || status === "failed") {
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => initiate(onAutoFill)}
          className="w-full py-[14px] rounded-[12px] text-[14px] font-bold text-white bg-gradient-to-r from-orange-500 to-red-600 hover:opacity-90 cursor-pointer transition-all"
        >
          Start Aadhaar KYC
        </button>
        {status === "failed" && error && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-[10px] bg-red-50 border border-red-200">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" className="mt-0.5 shrink-0">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p className="text-[12px] font-medium text-red-600 flex-1">{error}</p>
            <button onClick={reset} className="text-[11px] font-bold text-red-500 underline cursor-pointer bg-transparent border-none shrink-0">
              Retry
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── initiating ──
  if (status === "initiating") {
    return (
      <button disabled className="w-full py-[14px] rounded-[12px] text-[14px] font-bold text-white bg-gradient-to-r from-orange-500 to-red-600 opacity-70 cursor-not-allowed flex items-center justify-center gap-2">
        <Spinner /> Initiating…
      </button>
    );
  }

  // ── verified ──
  if (status === "verified") {
    return (
      <div className="flex items-center gap-2 px-4 py-[14px] rounded-[12px] bg-green-50 border border-green-200">
        <CheckIcon />
        <span className="text-[14px] font-bold text-green-700">Aadhaar Verified ✅</span>
        {digioVerified && (
          <span className="text-[11px] font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">via Digio</span>
        )}
      </div>
    );
  }

  // ── iframe / polling — verification window opened ──
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-4 py-[14px] rounded-[12px] bg-amber-50 border border-amber-200">
        <Spinner />
        <span className="text-[13px] font-semibold text-amber-700">
          {digioVerified ? "Digio verified — finalising…" : "Verification window opened — complete KYC there"}
        </span>
      </div>

      {/* Reopen button if user closed the window */}
      <button
        type="button"
        onClick={openDigilocker}
        className="w-full py-[11px] rounded-[12px] text-[13px] font-bold text-white bg-gradient-to-r from-violet-600 to-purple-700 hover:opacity-90 cursor-pointer transition-all flex items-center justify-center gap-2"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
        </svg>
        Reopen Verification Window
      </button>
    </div>
  );
}
